import {kebabCase} from 'lodash-es';
import {useState} from 'react';

import {fileService, kycBucketKey} from '@/services/fileService';
import {rfiService} from '@/services/rfiService';
import type {BusinessRFI} from '@/types';
import {BusinessRFIStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

export const useBusinessRFIActions = (rfi: BusinessRFI) => {
  const [isResolving, setIsResolving] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Match the admin convention (pool accounts / tiers): reload after a successful mutation.
  const reload = () => setTimeout(() => window.location.reload(), 1000);

  const resolve = async () => {
    setIsResolving(true);
    try {
      await rfiService.updateBusinessRFI(rfi.id, {
        status: BusinessRFIStatus.Resolved,
      });
      showSuccessMessage('RFI resolved');
      reload();
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error resolving RFI'));
    } finally {
      setIsResolving(false);
    }
  };

  const requestReupload = async (
    note: string,
    documentsToReupload: string[],
  ) => {
    setIsRequesting(true);
    try {
      await rfiService.updateBusinessRFI(rfi.id, {
        status: BusinessRFIStatus.ReuploadRequested,
        documentsToReupload,
        note: note?.trim() || undefined,
      });
      showSuccessMessage('Re-upload requested');
      reload();
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error requesting re-upload'));
    } finally {
      setIsRequesting(false);
    }
  };

  // Naming the documents on a processor-raised draft goes through
  // SendBusinessRFIModal / useBusinessRFISend instead of an action here: it is
  // a PATCH with its own submit/loading lifecycle, the same shape as the
  // create-RFI form it now shares a component with.

  // [D3] sanitize only the S3 object name; the documentType label stays the map key.
  const buildS3Key = (documentType: string, file: File): string => {
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    return `rfi/${rfi.businessId}/${rfi.id}/${kebabCase(documentType)}-${Date.now()}${
      ext ? `.${ext}` : ''
    }`;
  };

  // Upload several documents in one flow: upload all files to S3, then a single PATCH with
  // the combined { documentType: s3Key } map. If any S3 upload fails, the PATCH is skipped.
  const uploadRFIDocs = async (
    entries: {documentType: string; file: File}[],
  ) => {
    if (!entries.length) {
      return;
    }
    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        entries.map(async ({documentType, file}) => {
          const s3Key = buildS3Key(documentType, file);
          await fileService.uploadFile(
            s3Key,
            kycBucketKey,
            rfi.businessId,
            file,
          );
          return [documentType, s3Key] as const;
        }),
      );
      await rfiService.updateBusinessRFI(rfi.id, {
        documents: Object.fromEntries(uploaded),
      });
      showSuccessMessage(
        entries.length > 1
          ? 'Documents uploaded'
          : `${entries[0].documentType} uploaded`,
      );
      reload();
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error uploading documents'));
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isResolving,
    isRequesting,
    isUploading,
    resolve,
    requestReupload,
    uploadRFIDocs,
  };
};

export default useBusinessRFIActions;
